import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Download, BookOpen, Loader2, CheckCircle, User, Mail, Phone } from 'lucide-react';
import DataProtectionCheckbox from './DataProtectionCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const EBOOK_URL = "https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/dcv498le_ebook_vilaplana_7claves_ley2025.pdf";

export default function EbookSection() {
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/leads`, {
        nombre: form.nombre,
        email: form.email,
        telefono: form.telefono,
        cargo: '',
        empresa: '',
        mensaje: 'Descarga eBook: 7 Claves Ley 2/2025',
        origen: 'ebook_7claves'
      });

      setDownloaded(true);
      window.open(EBOOK_URL, '_blank');
    } catch (err) {
      if (!err.response) {
        setError('Error de conexión. Compruebe su conexión a internet.');
      } else {
        setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Error al procesar. Inténtelo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-slate-900" data-testid="ebook-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Info */}
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-rose-400">eBook gratuito</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mt-4 leading-tight">
              7 Claves Esenciales sobre la Ley 2/2025
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Todo lo que trabajadores, empresas y servicios de prevención necesitan saber sobre el Protocolo de Retorno al Trabajo.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                'Fin de la extinción automática del contrato por IP',
                'Plazos clave: 10 días y 3 meses',
                'Ajustes razonables y carga excesiva',
                'Nuevo papel de los servicios de prevención',
                'Riesgos psicosociales y salud mental',
                'Sentencia STS 563/2025 y responsabilidad penal',
                'Checklist de cumplimiento para empresas'
              ].map((item, i) => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
                  <CheckCircle size={16} className="text-rose-400 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Form */}
          <div className="bg-white p-8 border border-slate-200" data-testid="ebook-form-container">
            {downloaded ? (
              <div className="text-center py-8" data-testid="ebook-success">
                <CheckCircle className="mx-auto text-emerald-600 mb-4" size={48} />
                <h3 className="text-xl font-bold text-slate-900 mb-2">¡Descarga iniciada!</h3>
                <p className="text-slate-600 text-sm mb-6">Si no se ha abierto automáticamente:</p>
                <a
                  href={EBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 text-white font-bold hover:bg-rose-700 transition"
                  data-testid="ebook-download-link"
                >
                  <Download size={18} />
                  Descargar eBook
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="ebook-form">
                <div className="flex items-center gap-3 mb-6">
                  <BookOpen className="text-rose-600" size={24} />
                  <h3 className="text-lg font-bold text-slate-900">Descargue su eBook gratuito</h3>
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 border border-red-200">{error}</p>
                )}

                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Nombre *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text" required
                      placeholder="Su nombre completo"
                      value={form.nombre}
                      onChange={(e) => setForm(f => ({ ...f, nombre: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                      data-testid="ebook-nombre"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Email *</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email" required
                      placeholder="Su email profesional"
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                      data-testid="ebook-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Móvil <span className="text-slate-400 normal-case">(opcional)</span></label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="tel"
                      placeholder="600 000 000"
                      value={form.telefono}
                      onChange={(e) => setForm(f => ({ ...f, telefono: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                      data-testid="ebook-telefono"
                    />
                  </div>
                </div>

                <DataProtectionCheckbox
                  checked={privacyAccepted}
                  onChange={setPrivacyAccepted}
                  id="ebook-privacy-consent"
                />

                <button
                  type="submit"
                  disabled={loading || !privacyAccepted}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-rose-600 text-white font-bold text-lg hover:bg-rose-700 transition disabled:opacity-50"
                  data-testid="ebook-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      <Download size={20} />
                      Descargar eBook gratis
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
